import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Progress,
  Radio,
  Space,
  Spin,
  Typography,
  Modal,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SendOutlined,
} from "@ant-design/icons";
import "./exam.css";

const { Title, Text } = Typography;

const EXAM_API_BASE =
  process.env.REACT_APP_EXAM_API_BASE_URL ||
  "http://localhost:5000/api/exams";


// ============================================================
// MULTILINGUAL DISPLAY HELPERS
//
// Excel currently stores visible text in one field like:
// English, ಕನ್ನಡ, हिंदी
//
// This groups comma-separated pieces by script so ordinary commas
// inside one language are less likely to create unnecessary lines.
// ============================================================

const detectLanguage = (value = "") => {
  const text = String(value);

  if (/[\u0C80-\u0CFF]/.test(text)) {
    return "kn";
  }

  if (/[\u0900-\u097F]/.test(text)) {
    return "hi";
  }

  return "en";
};

const LANGUAGE_LABELS = {
  en: "English",
  kn: "ಕನ್ನಡ",
  hi: "हिंदी",
};

const splitMultilingualText = (value) => {
  const text = String(value ?? "").trim();

  if (!text) {
    return [];
  }

  const pieces = text
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (pieces.length <= 1) {
    const lang = detectLanguage(text);

    return [
      {
        lang,
        label: LANGUAGE_LABELS[lang],
        text,
      },
    ];
  }

  const grouped = [];

  pieces.forEach((piece) => {
    const lang = detectLanguage(piece);
    const last = grouped[grouped.length - 1];

    if (last && last.lang === lang) {
      last.text = `${last.text}, ${piece}`;
    } else {
      grouped.push({
        lang,
        label: LANGUAGE_LABELS[lang],
        text: piece,
      });
    }
  });

  return grouped;
};

const MultilingualText = ({
  value,
  variant = "question",
}) => {
  const parts = splitMultilingualText(value);

  if (!parts.length) {
    return null;
  }

  return (
    <div
      className={`exam-language-stack exam-language-stack-${variant}`}
    >
      {parts.map((part, index) => (
        <div
          key={`${part.lang}-${index}`}
          className={`exam-language-row exam-language-${part.lang}`}
        >
          <span className="exam-language-label">
            {part.label}
          </span>

          <span className="exam-language-text">
            {part.text}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function ExamTestPage() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(null);

  const autoSubmitStartedRef = useRef(false);

  const userId = localStorage.getItem("exam_user_id");
  const deptId = localStorage.getItem("exam_dept_id");
  const unitCode = localStorage.getItem("exam_unit_code");
  const unitName = localStorage.getItem("exam_unit_name");
  const attemptId = localStorage.getItem("exam_attempt_id");

  useEffect(() => {
    if (!userId || !deptId || !unitCode || !unitName || !attemptId) {
      window.location.href = "/exam";
      return;
    }

    try {
      const storedExam = JSON.parse(
        localStorage.getItem("current_exam") || "{}"
      );

      if (!storedExam?.exam_id || !Array.isArray(storedExam?.questions)) {
        window.location.href = "/exam";
        return;
      }

      setExam(storedExam);
      setQuestions(storedExam.questions);

      const initialAnswers = {};
      storedExam.questions.forEach((question) => {
        initialAnswers[question.question_id] = null;
      });

      try {
        const savedAnswers = JSON.parse(
          localStorage.getItem(`exam_answers_${attemptId}`) || "{}"
        );

        setAnswers({
          ...initialAnswers,
          ...savedAnswers,
        });
      } catch {
        setAnswers(initialAnswers);
      }

      if (storedExam.expires_at) {
        const expiresAtMs = new Date(storedExam.expires_at).getTime();

        const remaining = Number.isFinite(expiresAtMs)
          ? Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000))
          : Number(storedExam.duration_minutes || 30) * 60;

        setRemainingSeconds(remaining);
      } else {
        setRemainingSeconds(
          Number(storedExam.duration_minutes || 30) * 60
        );
      }
    } catch (error) {
      console.error("Current exam parse error:", error);
      window.location.href = "/exam";
      return;
    } finally {
      setLoading(false);
    }
  }, []);

  const currentQuestion = questions[currentIndex];

  const answeredCount = useMemo(
    () => Object.values(answers).filter(Boolean).length,
    [answers]
  );

  const progress = questions.length
    ? Math.round((answeredCount / questions.length) * 100)
    : 0;

  const handleAnswer = (questionId, value) => {
    setAnswers((previous) => {
      const updated = { ...previous, [questionId]: value };
      localStorage.setItem(
        `exam_answers_${attemptId}`,
        JSON.stringify(updated)
      );
      return updated;
    });
  };

  const formatTime = (totalSeconds) => {
    const safeSeconds = Math.max(0, Number(totalSeconds || 0));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;

    if (hours > 0) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
      )}:${String(seconds).padStart(2, "0")}`;
    }

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  const handleSubmit = async (autoSubmit = false) => {
    if (submitting) return;

    try {
      setSubmitting(true);

      const submittedAnswers = questions.map((question) => ({
        question_id: Number(question.question_id),
        selected_answer: answers[question.question_id] || null,
      }));

      const response = await fetch(
        `${EXAM_API_BASE}/attempts/${attemptId}/submit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: Number(userId),
            dept_id: Number(deptId),
            unit_code: unitCode,
            unit_name: unitName,
            auto_submit: autoSubmit,
            answers: submittedAnswers,
          }),
        }
      );

      const result = await response.json();

      if (!result?.status) {
        if (result?.message === "Attempt not found") {
          localStorage.removeItem("current_exam");
          localStorage.removeItem("exam_attempt_id");
          localStorage.removeItem(`exam_answers_${attemptId}`);

          Modal.error({
            title: "Test Session Expired",
            content:
              "This test attempt no longer exists. Please start the assessment again.",
            onOk: () => {
              window.location.href = "/exam";
            },
          });
          return;
        }

        Modal.error({
          title: "Submission Failed",
          content: result?.message || "Unable to submit test",
        });
        return;
      }

      localStorage.setItem(
        "exam_result",
        JSON.stringify({
          ...(result.data || {}),
          auto_submitted: autoSubmit,
        })
      );

      localStorage.removeItem("current_exam");
      localStorage.removeItem("exam_attempt_id");
      localStorage.removeItem(`exam_answers_${attemptId}`);

      window.location.href = `/exam/result/${attemptId}`;
    } catch (error) {
      console.error("Submit exam error:", error);

      Modal.error({
        title: "Submission Failed",
        content: autoSubmit
          ? "Time is complete, but the test could not be submitted. Please check the connection and submit again."
          : "Unable to submit the test. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmSubmit = () => {
    const unanswered = questions.length - answeredCount;

    Modal.confirm({
      title: "Submit Test?",
      content:
        unanswered > 0
          ? `${unanswered} question(s) are unanswered. Do you still want to submit?`
          : "You have answered all questions. Do you want to submit the test?",
      okText: "Submit Test",
      cancelText: "Continue Test",
      onOk: () => handleSubmit(false),
    });
  };

  useEffect(() => {
    if (loading || remainingSeconds === null || submitting) return;

    if (remainingSeconds <= 0) {
      if (!autoSubmitStartedRef.current && questions.length > 0) {
        autoSubmitStartedRef.current = true;
        handleSubmit(true);
      }
      return;
    }

    const timer = window.setTimeout(() => {
      setRemainingSeconds((previous) =>
        Math.max(0, Number(previous || 0) - 1)
      );
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [remainingSeconds, loading, submitting, questions.length]);

  const getQuestionOptions = (question) => {
    if (!question) return [];

    return [
      { key: "A", value: question.option_a },
      { key: "B", value: question.option_b },
      { key: "C", value: question.option_c },
      { key: "D", value: question.option_d },
      { key: "E", value: question.option_e },
    ].filter(
      (option) =>
        option.value !== null &&
        option.value !== undefined &&
        String(option.value).trim() !== ""
    );
  };

  if (loading) {
    return (
      <div className="exam-loading-page">
        <Spin size="large" />
        <div className="exam-loading-text">Loading test...</div>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div className="exam-no-questions">No questions available.</div>;
  }

  const currentOptions = getQuestionOptions(currentQuestion);
  const timerCritical = Number(remainingSeconds || 0) <= 300;

  return (
    <div className="exam-test-page">
      <header className="exam-test-simple-header">
        <div className="exam-test-simple-inner">
          <div className="exam-test-brand-group">
            <img
              src="/logo192.png"
              alt="SM-ART"
              className="exam-test-brand-logo"
            />
            <div>
              <Title level={4}>
                {exam?.exam_name || "Assessment"}
              </Title>
              <Text>Attempt {exam?.attempt_no || 1}</Text>
            </div>
          </div>

          <div className="exam-test-top-meta">
            <div className={`exam-simple-timer ${timerCritical ? "critical" : ""}`}>
              <ClockCircleOutlined />
              <div>
                <span>Time Remaining</span>
                <strong>
                  {remainingSeconds !== null
                    ? formatTime(remainingSeconds)
                    : "--:--"}
                </strong>
              </div>
            </div>

            <div className="exam-simple-count">
              <strong>{answeredCount}/{questions.length}</strong>
              <span>Answered</span>
            </div>
          </div>
        </div>

        <div className="exam-simple-progress-wrap">
          <Progress
            percent={progress}
            showInfo={false}
            strokeWidth={5}
            strokeColor="#163b65"
            trailColor="#ececf2"
          />
        </div>
      </header>

      <main className="exam-test-simple-layout">
        <section className="exam-question-panel exam-test-surface">
          <div className="exam-question-topline">
            <span>Question {currentIndex + 1}</span>
            <span>{currentQuestion.question_type || "MCQ"}</span>
            <span>{currentQuestion.marks || 1} Mark</span>
          </div>

          <div className="exam-question-text-simple">
            <MultilingualText
              value={currentQuestion.question}
              variant="question"
            />
          </div>

          <Radio.Group
            value={answers[currentQuestion.question_id]}
            onChange={(event) =>
              handleAnswer(currentQuestion.question_id, event.target.value)
            }
            className="exam-options-group"
          >
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              {currentOptions.map((option) => {
                const selected =
                  answers[currentQuestion.question_id] === option.key;

                return (
                  <div
                    key={option.key}
                    className={`exam-option-simple ${selected ? "selected" : ""}`}
                    onClick={() =>
                      handleAnswer(currentQuestion.question_id, option.key)
                    }
                  >
                    <Radio value={option.key}>
                      <span className="exam-option-content">
                        <strong className="exam-option-key">
                          {option.key}.
                        </strong>

                        <MultilingualText
                          value={option.value}
                          variant="option"
                        />
                      </span>
                    </Radio>
                  </div>
                );
              })}
            </Space>
          </Radio.Group>

          <div className="exam-question-actions-simple">
            <Button
              icon={<ArrowLeftOutlined />}
              disabled={currentIndex === 0 || submitting}
              onClick={() => setCurrentIndex((p) => p - 1)}
            >
              Previous
            </Button>

            {currentIndex < questions.length - 1 ? (
              <Button
                type="primary"
                disabled={submitting}
                onClick={() => setCurrentIndex((p) => p + 1)}
                className="exam-gradient-button"
              >
                Next <ArrowRightOutlined />
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={submitting}
                onClick={confirmSubmit}
                className="exam-gradient-button"
              >
                Submit Test
              </Button>
            )}
          </div>
        </section>

        <aside className="exam-question-side exam-test-side-surface">
          <h3>Questions</h3>
          <p>Select a question to review.</p>

          <div className="exam-question-grid-simple">
            {questions.map((question, index) => {
              const answered = Boolean(answers[question.question_id]);
              const active = currentIndex === index;

              return (
                <button
                  key={question.question_id}
                  type="button"
                  className={`exam-question-pill ${active ? "active" : ""} ${
                    answered && !active ? "answered" : ""
                  }`}
                  onClick={() => setCurrentIndex(index)}
                >
                  {answered && !active ? <CheckCircleOutlined /> : index + 1}
                </button>
              );
            })}
          </div>

          <div className="exam-side-summary-simple">
            <div><span>Answered</span><strong>{answeredCount}</strong></div>
            <div><span>Unanswered</span><strong>{questions.length - answeredCount}</strong></div>
            <div><span>Total</span><strong>{questions.length}</strong></div>
          </div>

          <Button
            block
            icon={<SendOutlined />}
            loading={submitting}
            onClick={confirmSubmit}
            className="exam-outline-submit"
          >
            Submit Test
          </Button>
        </aside>
      </main>
    </div>
  );
}
